import React, { useState } from 'react';
import {
  Box,
  FormControlLabel,
  Checkbox,
  Typography,
  Chip,
  Tooltip,
  Paper
} from '@mui/material';
import { Info, CheckCircle } from '@mui/icons-material';

/**
 * InteractiveSuggestions
 * Displays AI-suggested items with checkboxes for selection
 */
const InteractiveSuggestions = ({ suggestions = [], onSelectionChange }) => {
  const [selected, setSelected] = useState([]);

  const handleToggle = (item) => {
    const newSelected = selected.includes(item.item)
      ? selected.filter(s => s !== item.item)
      : [...selected, item.item];
    
    setSelected(newSelected);
    if (onSelectionChange) {
      onSelectionChange(newSelected);
    }
  };

  const getImportanceColor = (importance) => {
    switch (importance) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  if (!suggestions || suggestions.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No suggestions available.
      </Typography>
    );
  }

  return (
    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
      <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
        <CheckCircle sx={{ mr: 1, fontSize: 20, color: 'primary.main' }} />
        AI Suggestions (Select any that apply):
      </Typography>
      
      <Box>
        {suggestions.map((suggestion, index) => (
          <Box key={index} sx={{ mb: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selected.includes(suggestion.item)}
                  onChange={() => handleToggle(suggestion)}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">{suggestion.item}</Typography>
                  <Chip 
                    label={suggestion.importance} 
                    size="small" 
                    color={getImportanceColor(suggestion.importance)}
                  />
                  <Tooltip title={suggestion.reason || 'AI suggested this item'} arrow>
                    <Info sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Box>
              }
            />
          </Box>
        ))}
      </Box>

      {selected.length > 0 && (
        <Box sx={{ mt: 2, p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
          <Typography variant="caption" color="success.dark">
            ✓ {selected.length} item(s) selected
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default InteractiveSuggestions;
